// src/pages/SummaryPage.jsx — end-of-season trophy case.
import React, { useMemo } from "react";

function StatCard({ icon, label, value, sub, gold }) {
  return (
    <div className="stat-card" style={{
      border: gold ? "1px solid rgba(245,166,35,0.35)" : undefined,
      background: gold ? "rgba(245,166,35,0.06)" : undefined,
    }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ fontSize: 18, color: gold ? "var(--gold)" : "var(--text)" }}>
        {value || "—"}
      </div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export function SummaryPage({ league, room }) {
  const schedule      = useMemo(() => league?.season?.schedule     || {}, [league]);
  const teamPoints    = useMemo(() => league?.season?.teamPoints   || {}, [league]);
  const playerPoints  = useMemo(() => league?.season?.playerPoints || {}, [league]);
  const playerMap     = room?.players || {};

  // ── Season-wide team stats ─────────────────────────────────────
  const teamSeasonTotals = useMemo(() => {
    const totals = {};
    for (const [week, pts] of Object.entries(teamPoints)) {
      if (Number(week) > 12) continue; // regular season only
      for (const [team, p] of Object.entries(pts)) {
        if (!totals[team]) totals[team] = { total: 0, weeks: [], best: 0, worst: Infinity };
        totals[team].total += p;
        totals[team].weeks.push({ week: Number(week), pts: p });
        if (p > totals[team].best) totals[team].best = p;
        if (p < totals[team].worst) totals[team].worst = p;
      }
    }
    return totals;
  }, [teamPoints]);

  // ── Season-wide player stats ───────────────────────────────────
  const playerSeasonTotals = useMemo(() => {
    const totals = {};
    for (const [week, pts] of Object.entries(playerPoints)) {
      if (Number(week) > 12) continue;
      for (const [pid, p] of Object.entries(pts)) {
        if (!totals[pid]) totals[pid] = { total: 0, best: 0, bestWeek: null };
        totals[pid].total += p;
        if (p > totals[pid].best) { totals[pid].best = p; totals[pid].bestWeek = Number(week); }
      }
    }
    return totals;
  }, [playerPoints]);

  // ── Standings ──────────────────────────────────────────────────
  const standings = useMemo(() => {
    const map = {};
    const ensure = (t) => { if (!map[t]) map[t] = { team: t, wins: 0, losses: 0, ties: 0, pf: 0, pa: 0 }; };
    Object.keys(schedule).map(Number).filter(w => w <= 12).forEach(wk => {
      (schedule[String(wk)]?.matchups || []).forEach(m => {
        const hp = Number(m.homePoints), ap = Number(m.awayPoints);
        if (!Number.isFinite(hp) || !Number.isFinite(ap)) return;
        ensure(m.home); ensure(m.away);
        map[m.home].pf += hp; map[m.home].pa += ap;
        map[m.away].pf += ap; map[m.away].pa += hp;
        if (hp > ap) { map[m.home].wins++; map[m.away].losses++; }
        else if (ap > hp) { map[m.away].wins++; map[m.home].losses++; }
        else { map[m.home].ties++; map[m.away].ties++; }
      });
    });
    return Object.values(map).sort((a, b) => b.wins - a.wins || b.pf - a.pf);
  }, [schedule]);

  // ── Headlines ──────────────────────────────────────────────────
  const champion = league?.season?.champion || (() => {
    const final = schedule["14"]?.matchups?.[0];
    if (!final || final.homePoints === null) return null;
    return final.homePoints > final.awayPoints ? final.home : final.away;
  })();

  const mostWins    = standings[0];
  const mostPF      = [...standings].sort((a, b) => b.pf - a.pf)[0];
  const mostPA      = [...standings].sort((a, b) => b.pa - a.pa)[0];

  // Best week ever (team)
  const bestWeekTeam = useMemo(() => {
    let best = { pts: 0, team: null, week: null };
    for (const [team, data] of Object.entries(teamSeasonTotals)) {
      for (const { week, pts } of data.weeks) {
        if (pts > best.pts) best = { pts, team, week };
      }
    }
    return best;
  }, [teamSeasonTotals]);

  // Highest single-week player score
  const bestPlayerWeek = useMemo(() => {
    let best = { pts: 0, pid: null, week: null };
    for (const [pid, data] of Object.entries(playerSeasonTotals)) {
      if (data.best > best.pts) best = { pts: data.best, pid, week: data.bestWeek };
    }
    return best;
  }, [playerSeasonTotals]);

  const topPlayer = useMemo(() => {
    return Object.entries(playerSeasonTotals)
      .sort((a, b) => b[1].total - a[1].total)[0];
  }, [playerSeasonTotals]);

  // H2H records
  const h2h = useMemo(() => {
    const records = {}; // "A|B" -> { A: wins, B: wins }
    const key = (a, b) => [a, b].sort().join("|");
    Object.keys(schedule).map(Number).filter(w => w <= 12).forEach(wk => {
      (schedule[String(wk)]?.matchups || []).forEach(m => {
        const hp = Number(m.homePoints), ap = Number(m.awayPoints);
        if (!Number.isFinite(hp) || !Number.isFinite(ap)) return;
        const k = key(m.home, m.away);
        if (!records[k]) records[k] = { [m.home]: 0, [m.away]: 0 };
        if (hp > ap) records[k][m.home]++;
        else if (ap > hp) records[k][m.away]++;
      });
    });
    return records;
  }, [schedule]);

  const teamNames = standings.map(r => r.team);
  const hasData   = standings.length > 0;

  if (!hasData) {
    return (
      <div className="panel" style={{ padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
        <div style={{ color: "var(--text-dim)" }}>
          Season summary will appear once regular season games are scored.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Champion banner */}
      {champion && (
        <div style={{
          textAlign: "center", padding: "24px 16px",
          background: "linear-gradient(135deg, rgba(245,166,35,0.12), rgba(245,166,35,0.04))",
          border: "1px solid rgba(245,166,35,0.3)",
          borderRadius: "var(--radius-lg)", marginBottom: 16,
        }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🏆</div>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase",
            color: "var(--gold)", marginBottom: 4 }}>Champion</div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, fontWeight: 900, color: "var(--gold)" }}>
            {champion}
          </div>
        </div>
      )}

      {/* Award cards */}
      <div className="stat-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", marginBottom: 16 }}>
        <StatCard icon="🥇" label="Most Wins"       value={mostWins?.team}    sub={`${mostWins?.wins}W–${mostWins?.losses}L`}          gold />
        <StatCard icon="⚡" label="Top Scorer"       value={mostPF?.team}      sub={`${mostPF?.pf} total pts`} />
        <StatCard icon="🏏" label="Top Player"
          value={topPlayer ? playerMap[topPlayer[0]]?.name || `#${topPlayer[0]}` : null}
          sub={topPlayer ? `${topPlayer[1].total} pts over season` : null} />
        <StatCard icon="🔥" label="Best Single Week"
          value={bestWeekTeam.team}
          sub={bestWeekTeam.pts ? `${bestWeekTeam.pts} pts (Wk ${bestWeekTeam.week})` : null} />
        <StatCard icon="🌟" label="Best Player Week"
          value={bestPlayerWeek.pid ? (playerMap[bestPlayerWeek.pid]?.name || `#${bestPlayerWeek.pid}`) : null}
          sub={bestPlayerWeek.pts ? `${bestPlayerWeek.pts} pts (Wk ${bestPlayerWeek.week})` : null} />
        <StatCard icon="🎯" label="Most Pts Against" value={mostPA?.team} sub={`${mostPA?.pa} pts conceded`} />
      </div>

      {/* Final standings */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-head">
          <div style={{ fontWeight: 700, fontSize: 14 }}>Final Regular Season Standings</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th><th>Team</th>
              <th style={{ textAlign: "center" }}>W</th>
              <th style={{ textAlign: "center" }}>L</th>
              <th style={{ textAlign: "center" }}>T</th>
              <th style={{ textAlign: "right" }}>PF</th>
              <th style={{ textAlign: "right" }}>PA</th>
              <th style={{ textAlign: "right" }}>Total Pts</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((r, i) => (
              <tr key={r.team} style={{ opacity: i < 4 ? 1 : 0.6 }}>
                <td><div className={`rank-badge ${i < 3 ? `rank-${i+1}` : ""}`}>{i+1}</div></td>
                <td style={{ fontWeight: 700 }}>
                  {r.team}
                  {i < 4 && <span className="pill green" style={{ marginLeft: 6, fontSize: 8 }}>PO</span>}
                </td>
                <td style={{ textAlign: "center", color: "var(--green)", fontWeight: 700 }}>{r.wins}</td>
                <td style={{ textAlign: "center", color: "var(--red)" }}>{r.losses}</td>
                <td style={{ textAlign: "center", color: "var(--text-dim)" }}>{r.ties}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>{r.pf}</td>
                <td style={{ textAlign: "right", color: "var(--text-dim)" }}>{r.pa}</td>
                <td style={{ textAlign: "right", fontWeight: 700, color: "var(--gold)" }}>
                  {teamSeasonTotals[r.team]?.total ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Head-to-head grid */}
      {teamNames.length > 1 && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-head">
            <div style={{ fontWeight: 700, fontSize: 14 }}>Head-to-Head Records</div>
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>Row beat column</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ minWidth: teamNames.length * 80 + 100 }}>
              <thead>
                <tr>
                  <th></th>
                  {teamNames.map(t => (
                    <th key={t} style={{ textAlign: "center", fontSize: 10, maxWidth: 70,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teamNames.map(rowTeam => (
                  <tr key={rowTeam}>
                    <td style={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>{rowTeam}</td>
                    {teamNames.map(colTeam => {
                      if (rowTeam === colTeam) return (
                        <td key={colTeam} style={{ textAlign: "center", background: "var(--bg2)", color: "var(--border2)" }}>–</td>
                      );
                      const k = [rowTeam, colTeam].sort().join("|");
                      const rec = h2h[k];
                      const wins = rec?.[rowTeam] ?? 0;
                      const losses = rec?.[colTeam] ?? 0;
                      return (
                        <td key={colTeam} style={{
                          textAlign: "center", fontWeight: 600, fontSize: 12,
                          color: wins > losses ? "var(--green)" : wins < losses ? "var(--red)" : "var(--text-dim)",
                        }}>
                          {wins}–{losses}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Week-by-week team scores */}
      <div className="panel">
        <div className="panel-head">
          <div style={{ fontWeight: 700, fontSize: 14 }}>Weekly Scores</div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: teamNames.length * 70 + 80 }}>
            <thead>
              <tr>
                <th>Team</th>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(w => (
                  <th key={w} style={{ textAlign: "center", fontSize: 10 }}>Wk {w}</th>
                ))}
                <th style={{ textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {standings.map(r => {
                const data = teamSeasonTotals[r.team] || { weeks: [], total: 0 };
                const wkMap = {};
                data.weeks.forEach(({ week, pts }) => { wkMap[week] = pts; });
                return (
                  <tr key={r.team}>
                    <td style={{ fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}>{r.team}</td>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(w => {
                      const pts = wkMap[w];
                      const isMax = pts === teamSeasonTotals[r.team]?.best;
                      return (
                        <td key={w} style={{
                          textAlign: "center", fontSize: 12,
                          color: pts === undefined ? "var(--text-muted)" : isMax ? "var(--gold)" : "var(--text)",
                          fontWeight: isMax ? 700 : 400,
                        }}>
                          {pts ?? "—"}
                        </td>
                      );
                    })}
                    <td style={{ textAlign: "right", fontWeight: 700, color: "var(--gold)" }}>
                      {data.total}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
